'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "ButtonContent", "buttonSize", "buttonUse", "closeOnTargetLeave", "disabled", "error", "id", "ignoreAccents", "ignoreCase", "inputRef", "menuRenderer", "menuWidth", "noResultsText", "onChange", "onInputValueChange", "onOpenChange", "onSelectedOptionChange", "open", "placeholder", "readOnly", "resetValue", "searchable", "searchClassName", "searchPlaceholder", "searchPromptText", "tabIndex", "valueComponent", "valueRenderer"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BackButton } from 'foundations-components/transitional/components/BackButton';
import { Controllable } from 'foundations-components/transitional/components/Controllable/Controllable';
import { Link } from 'foundations-components/transitional/components/Link';
import { PLACEMENTS } from 'foundations-components/transitional/components/Tooltip/placementConstants';
import { View } from 'foundations-components/transitional/components/View';
import { FieldsetContextConsumer } from 'foundations-components/transitional/context';
import { OptionOrGroupType } from './DrilldownSelect.types';
import { sequence } from 'foundations-components/transitional/utils/functions';
import { lazyEval } from 'foundations-components/transitional/utils/lazyEval';
import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import Select from 'react-select-plus';
import devLogger from 'react-utils/devLogger';
import DrilldownResultsItem from './DrilldownResultsItem';
import { ButtonUsesType, ValueType } from './DrilldownSelect.types';
import DrilldownSelectWithButtonAnchor from './DrilldownSelectWithButtonAnchor';

// Import extracted modules
import { getResetOption, initializeOptions } from './DrilldownSelect.helpers';
import { createHandlers } from './DrilldownSelect.handlers';
import { DEFAULT_PROPS, INITIAL_STATE } from './DrilldownSelect.constants';
import { getOptions, getMenuWidth, computeControlWidth, getNoResultsText } from './DrilldownSelect.getters';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  stripDiacritics
} = Select;
class DrilldownSelectCore extends Component {
  static getDerivedStateFromProps(props, state) {
    const resetOption = getResetOption(props, state);
    let openingState;
    let closingState;
    let prevOpen;
    const value = props.value;
    if (!state.prevOpen && props.open) {
      openingState = initializeOptions(props, state);
    } else if (state.prevOpen && !props.open) {
      closingState = {
        inputValue: '',
        initiallyOpen: false
      };
    }
    if (state.prevOpen !== props.open) {
      prevOpen = {
        prevOpen: props.open
      };
    }
    return Object.assign({
      value: value !== '' ? value : null
    }, resetOption, openingState, closingState, prevOpen);
  }
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this._buttonRef = /*#__PURE__*/createRef();
    this.updateControlWidth = () => {
      var _this$_buttonRef$curr;
      const {
        controlWidth,
        target
      } = this.state;
      const controlEl = (_this$_buttonRef$curr = this._buttonRef.current) !== null && _this$_buttonRef$curr !== void 0 ? _this$_buttonRef$curr : null;
      const newControlWidth = computeControlWidth(controlEl, this.props.open);
      if (target !== controlEl || controlWidth !== newControlWidth) {
        this.setState({
          controlWidth: newControlWidth,
          target: controlEl
        });
      }
    };
    this.popoverRef = /*#__PURE__*/createRef();
    this.optionRendererProxy = option => {
      const {
        optionRenderer
      } = this.props;
      if (optionRenderer == null) {
        return this.defaultOptionRenderer(option);
      }
      return optionRenderer(option);
    };
    this.defaultValueRenderer = option => {
      const {
        itemComponent,
        valueComponent
      } = this.props;
      return /*#__PURE__*/_jsx(DrilldownResultsItem, {
        "aria-invalid": option.invalid,
        itemComponent: valueComponent || itemComponent,
        option: Object.assign({}, option, {
          help: undefined
        }),
        _useButton: false,
        _useDropdownText: false
      }, option.value);
    };
    this.filterOptionsProxy = options => {
      return options;
    };
    this.focus = () => {
      try {
        this._anchorRef.focus();
      } catch (e) {
        /* ¯\_(ツ)_/¯ */
      }
    };
    this.anchorRefCallback = anchorRef => {
      this._anchorRef = anchorRef;
    };
    this.selectRefCallback = () => {
      // Hackily override Select's selectValue and handleMouseDownOnMenu to support "Load more"
      const internalSelect = this.getInternalSelect();
      if (!internalSelect) return;
      internalSelect.selectValue = this.selectValueProxy.bind(this, internalSelect.selectValue);
    };
    this.getInternalSelect = () => {
      // Return our react-select Select instance, or null
      if (!this._anchorRef) {
        return null;
      }
      return this._anchorRef.getInternalSelect();
    };
    const resetOption = getResetOption(props, this.state);
    this.state = Object.assign({}, this.state, resetOption, {
      initiallyOpen: props.open,
      prevOpen: props.open
    });

    // Initialize handlers
    this.handlers = createHandlers(this);

    // Bind handlers
    this.handleChange = this.handlers.handleChange;
    this.handleInputKeyDown = this.handlers.handleInputKeyDown;
    this.triggerOpen = this.handlers.triggerOpen;
    this.handleOpenChange = this.handlers.handleOpenChange;
    this.handleInputChange = this.handlers.handleInputChange;
    this.handleInputValueChange = this.handlers.handleInputValueChange;
    this.handleAutosize = this.handlers.handleAutosize;
  }
  componentDidMount() {
    this.updateControlWidth();
  }
  componentDidUpdate(prevProps) {
    this.updateControlWidth();
    if (!prevProps.open && this.props.open) {
      this.focus();
    }
  }
  getOptions() {
    return getOptions(this);
  }
  defaultOptionRenderer(option) {
    if (option.hide) {
      return null;
    }
    const {
      itemComponent,
      optionComponent
    } = this.props;
    return /*#__PURE__*/_jsx(DrilldownResultsItem, {
      itemComponent: optionComponent || itemComponent,
      option: option
    });
  }
  // Extracted to a function so it can be stubbed out in tests
  getDefaultFilterOptionsFunc() {
    return Select.defaultProps.filterOptions;
  }
  selectValueProxy(selectValue, selectedOption) {
    selectValue(selectedOption);
  }
  getMenuWidth() {
    return getMenuWidth(this);
  }
  getNoResultsText({
    noResultsText
  }) {
    return getNoResultsText(this, {
      noResultsText
    });
  }
  renderBackButton() {
    const {
      rootBreadcrumb
    } = this.props;
    const {
      inputValue,
      optionsStack
    } = this.state;
    if (inputValue || optionsStack.length <= 1) {
      return null;
    }
    const backButtonLabel = rootBreadcrumb ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "data-token-picker.drilldownSelect.backBreadcrumbOption",
      options: {
        breadcrumbPath: rootBreadcrumb
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "data-token-picker.drilldownSelect.backOption"
    });
    return /*#__PURE__*/_jsx(View, {
      paddingBlock: 400,
      paddingInline: 500,
      borderBlockEndWidth: "100",
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-default",
      children: /*#__PURE__*/_jsx(BackButton, {
        display: "inline",
        children: /*#__PURE__*/_jsx(Link, {
          onClick: () => {
            optionsStack.pop();
            this.setState({
              optionsStack
            });
          },
          children: backButtonLabel
        })
      })
    });
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        ButtonContent,
        buttonSize,
        buttonUse,
        closeOnTargetLeave,
        disabled,
        error,
        id,
        ignoreAccents,
        ignoreCase,
        inputRef,
        menuRenderer,
        menuWidth,
        noResultsText,
        onOpenChange,
        open,
        placeholder = () => I18n.text('data-token-picker.drilldownSelect.placeholder'),
        readOnly,
        resetValue,
        searchable,
        searchClassName,
        searchPlaceholder = () => I18n.text('data-token-picker.drilldownSelect.placeholder'),
        searchPromptText = () => I18n.text('data-token-picker.drilldownSelect.typeToSearch'),
        tabIndex,
        valueRenderer
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      initiallyOpen,
      resetOption,
      value
    } = this.state;

    // A null or undefined value should be considered equivalent to resetValue
    const computedValue = value == null ? resetValue : value;
    if (rest.autofocus) {
      devLogger.warn({
        message: "Select: The autofocus prop is deprecated in favor of autoFocus to match React's autoFocus prop",
        key: 'Select: autofocus'
      });
    }

    // We need to attach two ref callbacks to the combined anchor.
    this._refSequence = this._refSequence || sequence(this.anchorRefCallback, this.selectRefCallback);
    const options = this.getOptions();
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: ({
        disabled: fieldDisabled
      }) => {
        const computedDisabled = disabled || fieldDisabled;
        // Props that are used regardless of anchor type
        const commonProps = Object.assign({}, rest, {
          backspaceRemoves: false,
          clearable: false,
          disabled: computedDisabled,
          filterOptions: this.filterOptionsProxy,
          id,
          ignoreAccents,
          ignoreCase,
          inputProps: {},
          isOpen: open && !readOnly && !!this.state.target,
          labelKey: 'text',
          menuRenderer,
          noResultsText: this.getNoResultsText({
            noResultsText
          }),
          onChange: this.handleChange,
          onInputChange: this.handleInputChange,
          onInputKeyDown: this.handleInputKeyDown,
          openAfterFocus: true,
          optionRenderer: this.optionRendererProxy,
          options,
          placeholder: lazyEval(placeholder),
          readOnly,
          ref: this.anchorRefCallback,
          buttonRef: this._buttonRef,
          renderInvalidValues: true,
          required: false,
          resetValue,
          searchable,
          searchPromptText: lazyEval(searchPromptText),
          tabIndex,
          tabSelectsValue: false,
          value: computedValue,
          valueComponent: Select.defaultProps.valueComponent,
          valueRenderer: valueRenderer || this.defaultValueRenderer
        });
        return /*#__PURE__*/_jsx(DrilldownSelectWithButtonAnchor, Object.assign({}, commonProps, {
          autoComplete: "off",
          autoFocus: autoFocus,
          buttonSize: buttonSize,
          ButtonContent: ButtonContent,
          buttonUse: buttonUse,
          closeOnTargetLeave: closeOnTargetLeave,
          error: error,
          initiallyOpen: initiallyOpen,
          inputRef: inputRef,
          menuWidth: menuWidth,
          onInputValueChange: this.handleInputValueChange,
          onOpenChange: onOpenChange,
          resetOption: resetOption,
          searchClassName: searchClassName,
          searchPlaceholder: lazyEval(searchPlaceholder),
          selectComponent: Select,
          selectRef: this.selectRefCallback,
          minimumSearchCount: 0,
          dropdownHeader: this.renderBackButton()
        }));
      }
    });
  }
}
DrilldownSelectCore.displayName = 'Select';
DrilldownSelectCore.defaultProps = DEFAULT_PROPS;
DrilldownSelectCore.propTypes = {
  autoFocus: PropTypes.bool,
  ButtonContent: DrilldownSelectWithButtonAnchor.propTypes.ButtonContent,
  buttonSize: PropTypes.oneOf(['medium', 'small', 'extra-small', 'default']),
  buttonUse: ButtonUsesType,
  className: PropTypes.string,
  clearable: PropTypes.bool.isRequired,
  closeOnTargetLeave: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  defaultValue: ValueType,
  disabled: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  dropdownFooter: PropTypes.node,
  error: PropTypes.bool,
  filterOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  id: PropTypes.string,
  ignoreAccents: PropTypes.bool,
  ignoreCase: PropTypes.bool,
  inputComponent: PropTypes.elementType,
  inputRef: PropTypes.any,
  inputValue: PropTypes.string,
  itemComponent: PropTypes.any,
  menuRenderer: PropTypes.func,
  menuStyle: PropTypes.object,
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  noResultsText: PropTypes.node,
  onChange: PropTypes.func,
  onInputKeyDown: PropTypes.func,
  onInputChange: PropTypes.func,
  onInputValueChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  onPaste: PropTypes.func,
  onSelectedOptionChange: PropTypes.func,
  open: PropTypes.bool,
  optionComponent: PropTypes.any,
  options: PropTypes.arrayOf(OptionOrGroupType),
  optionRenderer: PropTypes.func,
  placeholder: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(PLACEMENTS),
  readOnly: PropTypes.bool,
  resetValue: ValueType,
  resultsClassName: PropTypes.string,
  rootBreadcrumb: PropTypes.string,
  searchable: PropTypes.bool.isRequired,
  searchClassName: PropTypes.string,
  searchPlaceholder: PropTypes.string.isRequired,
  searchPromptText: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
  value: PropTypes.string,
  valueComponent: PropTypes.any,
  valueRenderer: PropTypes.func
};
const DrilldownSelect = Controllable(DrilldownSelectCore, ['open']);
DrilldownSelect.stripDiacritics = stripDiacritics;
export default DrilldownSelect;